'use client';

import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
} from 'recharts';
import { useCountry } from '@/context/CountryContext';

interface TooltipPayload {
  name: string;
  value: number;
  payload: {
    country: string;
    access: number;
    program: string;
  };
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayload[];
  label?: string;
}

function CustomTooltip({ active, payload }: CustomTooltipProps) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0]?.payload;
  return (
    <div className="bg-surface-card border border-surface-border rounded-lg px-3 py-2 shadow-card-md text-xs">
      <p className="font-semibold text-text-main mb-1">{d?.country}</p>
      <p className="text-text-muted">Program: <span className="font-medium text-text-main">{d?.program}</span></p>
      <p className="text-text-muted">
        Access:{' '}
        <span className="font-bold text-status-green">{d?.access}%</span>
      </p>
      <p className="text-text-muted">
        Gap:{' '}
        <span className="font-medium text-status-red">{100 - (d?.access || 0)}%</span>
      </p>
    </div>
  );
}

export default function DashboardWaepChart() {
  const { allCountries, profile } = useCountry();

  const chartData = allCountries.map((c) => ({
    country: c.country === "Cote d'Ivoire" ? "Cote d'Ivoire" : c.country,
    access: c.accessPercent,
    gap: 100 - c.accessPercent,
    program: c.program,
  }));

  return (
    <div className="h-[260px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          margin={{ top: 8, right: 8, left: -20, bottom: 0 }}
          barSize={18}
        >
          <defs>
            <linearGradient id="accessGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--status-green)" stopOpacity={1} />
              <stop offset="100%" stopColor="var(--status-green)" stopOpacity={0.7} />
            </linearGradient>
            <linearGradient id="accessGradSelected" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--accent)" stopOpacity={1} />
              <stop offset="100%" stopColor="var(--accent)" stopOpacity={0.7} />
            </linearGradient>
            <linearGradient id="gapGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--muted)" stopOpacity={0.8} />
              <stop offset="100%" stopColor="var(--muted)" stopOpacity={0.4} />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="var(--border)"
            vertical={false}
          />
          <XAxis
            dataKey="country"
            tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }}
            axisLine={false}
            tickLine={false}
            interval={0}
            width={60}
          />
          <YAxis
            domain={[0, 100]}
            tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => `${v}%`}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
          <ReferenceLine
            y={100}
            stroke="var(--accent)"
            strokeDasharray="4 4"
            strokeWidth={1.5}
            label={{ value: 'Target', position: 'insideTopRight', fontSize: 9, fill: 'var(--accent)' }}
          />
          <Bar dataKey="access" stackId="a" radius={[0, 0, 0, 0]}>
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-access-${index}`}
                fill={entry.country === profile.name ? 'url(#accessGradSelected)' : 'url(#accessGrad)'}
              />
            ))}
          </Bar>
          <Bar dataKey="gap" stackId="a" fill="url(#gapGrad)" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}